/**
 * constants.ts
 *
 * All static constants for the DistillPageV2 feature.
 * Imported by the orchestrator and step components as needed.
 */

import type { AngleKey } from "./types";

// ── History ───────────────────────────────────────────────────────────────────

/** Maximum number of undo states kept per session. Older entries are dropped. */
export const HISTORY_MAX_DEPTH = 20;

// ── Angle picker ──────────────────────────────────────────────────────────────

/** The five tone-of-voice angles available in the Insight step. */
export const ANGLES: { label: string; key: AngleKey }[] = [
  { label: "Thoughtful", key: "thoughtful" },
  { label: "Contrarian", key: "contrarian" },
  { label: "Bold",       key: "bold"       },
  { label: "Viral",      key: "viral"      },
];

// ── Carousel loading ──────────────────────────────────────────────────────────

/** Rotating messages shown inside the summoning animation while the PDF generates. */
export const CRAFTING_MSGS = [
  "Selecting the perfect template…",
  "Layering your ideas into slides…",
  "Applying the perfect theme…",
  "Adding the finishing touches…",
  "Almost ready…",
];

// ── Template picker ───────────────────────────────────────────────────────────

/**
 * Maps each template id to the path of its static thumbnail PNG.
 *
 * The PNGs live in `frontend/public/templates/<id>-thumb.png` and are
 * pre-rendered from the corresponding sample PDF (page 1 only) by the script
 * `Node/src/scripts/generate-template-thumbnails.ts`. Re-run that script
 * after updating any sample PDF or adding a new template here.
 *
 * Only templates that appear in this map are shown in the V2 picker grid.
 *
 * Why pre-rendered PNGs instead of live PDF parsing in the browser:
 *   - 3× smaller payload (~2 MB vs ~6 MB of PDFs for first picker open)
 *   - No PDF.js worker, no canvas DPR math, no race conditions, no cancellation cascades
 *   - Native browser <img> rendering — paints as bytes arrive
 *   - See RI-008 for the PDF.js-in-grid bugs this redesign retired
 */
export const TEMPLATE_THUMBNAIL_IMAGES: Record<string, string> = {
  "pink":                 "/templates/pink-thumb.png",
  "sunday-mood":          "/templates/sunday-mood-thumb.png",
  "marketing-tips":       "/templates/marketing-tips-thumb.png",
  "colorshift":           "/templates/colorshift-thumb.png",
  "colorshift-framework": "/templates/colorshift-framework-thumb.png",
  "colorshift-headers":   "/templates/colorshift-headers-thumb.png",
};

/**
 * Short human-readable descriptions shown under each template card.
 * The API doesn't return these yet, so they're hardcoded here.
 */
export const TEMPLATE_DESCRIPTIONS: Record<string, string> = {
  "pink":                 "Bold hook + 6-point list. Great for quick playbooks and listicles.",
  "sunday-mood":          "Powerful headings only — no body text. Pure brevity for high-impact statements.",
  "marketing-tips":       "Hook + 7 slides, each with a headline and a short explanation paragraph.",
  "colorshift":           "8-slide framework with rotating accent colors. Hook → points → CTA.",
  "colorshift-framework": "Hook → interrupt → body steps → CTA. Ideal for structured thinking and models.",
  "colorshift-headers":   "Bold rotating-color slides — headlines only, no body text. Punchy single-thought-per-slide.",
};

// ── Template categories ───────────────────────────────────────────────────────

export type TemplateCategoryKey = "all" | "tips" | "explainer" | "framework";

/** Filter tabs shown above the template grid. */
export const TEMPLATE_CATEGORIES: { key: TemplateCategoryKey; label: string; hint: string }[] = [
  { key: "all",       label: "All",             hint: "" },
  { key: "tips",      label: "Tips & Lists",     hint: "Short punchy slides — numbered points, no body text" },
  { key: "explainer", label: "With Explanation", hint: "Each point gets a headline + a short paragraph" },
  { key: "framework", label: "Frameworks",       hint: "Structured arguments, mental models, step-by-step logic" },
];

/** Maps each template id to its category bucket. */
export const TEMPLATE_CATEGORY_MAP: Record<string, TemplateCategoryKey> = {
  "pink":                 "tips",
  "sunday-mood":          "tips",
  "marketing-tips":       "explainer",
  "colorshift":           "framework",
  "colorshift-framework": "framework",
  "colorshift-headers":   "tips",
};
